import { Type } from '@angular/core';

import { FRONTEND_PERMISSIONS } from '../permissions.constants';

import { DashboardAiActivationsComponent } from './dashboard-ai-activations/dashboard-ai-activations.component';
import { DashboardBillingComponent } from './dashboard-billing/dashboard-billing.component';
import { DashboardEngineComponent } from './dashboard-engine/dashboard-engine.component';
import { DashboardOpsReadinessComponent } from './dashboard-ops-readiness/dashboard-ops-readiness.component';
import { DashboardOtrLaborTrackingComponent } from './dashboard-otr-labor-tracking/dashboard-otr-labor-tracking.component';
import { DashboardOtrOpsReadinessComponent } from './dashboard-otr-ops-readiness/dashboard-otr-ops-readiness.component';
import { DashboardOtrSystemRevenueComponent } from './dashboard-otr-system-revenue/dashboard-otr-system-revenue.component';
import { DashboardRevenueComponent } from './dashboard-revenue/dashboard-revenue.component';
import { DashboardSalesLeaderboardComponent } from './dashboard-sales-leaderboard/dashboard-sales-leaderboard.component';
import { DashboardSelfImprovingAIComponent } from './dashboard-self-improving-ai/dashboard-self-improving-ai.component';
import { DashboardSystemRevenueComponent } from './dashboard-system-revenue/dashboard-system-revenue.component';
import { DashboardTransactionsComponent } from './dashboard-transactions/dashboard-transactions.component';

export const YMM_BRAND_ZONE_ID = 'ymm';
export const OTR_INTERSTATE_ZONE_ID = 'ymm-interstate-usa';

/**
 * Zone types where a dashboard may be shown. Used by `visibleInZoneTypes` to
 * gate dashboards by zone type rather than specific zone IDs (e.g. system
 * revenue is only shown in `corporate` and `root` zones).
 *
 * Mirrors the `type` field on the Zone GraphQL type (see branding.graphql).
 */
export type DashboardZoneType = 'root' | 'corporate' | 'franchise' | 'area' | 'branch' | 'business-unit' | 'business-group' | string;

export interface DashboardDisplayOptions {
  showTitle?: boolean;
  showPeriodSelectors?: boolean;
  showComparedTo?: boolean;
  showCurrency?: boolean;
  showLastUpdated?: boolean;
  showAutoReload?: boolean;
  showRefresh?: boolean;
}

export interface DashboardEntry {
  name: string;
  slug: string;
  icon: string;
  component: Type<any>;
  /** Single permission string (legacy). */
  permission?: string;
  /** Array of permissions — user must have ALL of them to see/access the dashboard. */
  permissionsRequired?: string[];
  helpId?: string;
  /** Zone IDs where this dashboard is visible. If omitted, visible in all zones. */
  visibleIn?: string[];
  /**
   * Zone types where this dashboard is visible (e.g. ['corporate', 'root']).
   * If omitted, visible in all zone types. Checked in addition to `visibleIn`
   * (both must pass). Used for dashboards like System Revenue that should
   * only appear in corporate zones, regardless of the specific zone ID.
   */
  visibleInZoneTypes?: DashboardZoneType[];
  /** When true, the dashboard is hidden from the dashboards page and search.
   *  Only accessible by direct link. */
  hidden?: boolean;
  display?: DashboardDisplayOptions;
}

const DEFAULT_DISPLAY: Required<DashboardDisplayOptions> = {
  showTitle: true,
  showPeriodSelectors: true,
  showComparedTo: true,
  showCurrency: true,
  showLastUpdated: true,
  showAutoReload: true,
  showRefresh: true,
};

export const DEFAULT_DASHBOARD_DISPLAY = DEFAULT_DISPLAY;

export function getDashboardDisplay(entry: DashboardEntry | undefined): Required<DashboardDisplayOptions> {
  return { ...DEFAULT_DISPLAY, ...entry?.display };
}

export function isDashboardVisibleInZone(entry: DashboardEntry, zoneId: string | undefined): boolean {
  if (!entry.visibleIn?.length) { return true; }
  if (!zoneId) { return false; }
  return entry.visibleIn.includes(zoneId);
}

/**
 * Check whether a dashboard is visible in the given zone TYPE (e.g.
 * 'corporate', 'root', 'franchise'). Returns true if the entry has no
 * `visibleInZoneTypes` filter. Used together with `isDashboardVisibleInZone`
 * — both must pass for a dashboard to be shown.
 */
export function isDashboardVisibleInZoneType(entry: DashboardEntry, zoneType: string | undefined): boolean {
  if (!entry.visibleInZoneTypes?.length) { return true; }
  if (!zoneType) { return false; }
  return entry.visibleInZoneTypes.includes(zoneType);
}

export function getDashboardPermissions(entry: DashboardEntry): string[] {
  const perms: string[] = [];
  if (entry.permission) { perms.push(entry.permission); }
  if (entry.permissionsRequired) { perms.push(...entry.permissionsRequired); }
  return perms;
}

export function isDashboardHidden(entry: DashboardEntry): boolean {
  return !!entry.hidden;
}

export const DASHBOARDS: DashboardEntry[] = [
  {
    name: 'Revenue',
    slug: 'revenue',
    icon: 'pi pi-chart-line',
    component: DashboardRevenueComponent,
    permissionsRequired: [FRONTEND_PERMISSIONS.dashboardRevenue],
  },
  {
    name: 'Engine',
    slug: 'engine',
    icon: 'pi pi-cog',
    component: DashboardEngineComponent,
    permissionsRequired: [FRONTEND_PERMISSIONS.dashboardEngine],
  },
  {
    name: 'Billing',
    slug: 'billing',
    icon: 'pi pi-wallet',
    component: DashboardBillingComponent,
    permissionsRequired: [FRONTEND_PERMISSIONS.dashboardBilling],
    helpId: 'dashboard-billing',
  },
  {
    name: 'Activations',
    slug: 'ai-activations',
    icon: 'pi pi-bolt',
    component: DashboardAiActivationsComponent,
    permissionsRequired: [FRONTEND_PERMISSIONS.dashboardActivations],
    helpId: 'dashboard-ai-activations',
  },
  {
    name: 'Sales Leaderboard',
    slug: 'sales-leaderboard',
    icon: 'pi pi-trophy',
    component: DashboardSalesLeaderboardComponent,
    permissionsRequired: [FRONTEND_PERMISSIONS.dashboardSalesLeaderboard],
  },
  {
    name: 'System Revenue',
    slug: 'system-revenue',
    icon: 'pi pi-money-bill',
    component: DashboardSystemRevenueComponent,
    permissionsRequired: [FRONTEND_PERMISSIONS.dashboardSystemRevenue],
    // Requirement: System Revenue is only shown in corporate and root zones.
    visibleInZoneTypes: ['corporate', 'root'],
  },
  {
    name: 'OTR - System Revenue',
    slug: 'otr-system-revenue',
    icon: 'pi pi-money-bill',
    component: DashboardOtrSystemRevenueComponent,
    permissionsRequired: [FRONTEND_PERMISSIONS.dashboardSystemRevenue],
    visibleIn: [YMM_BRAND_ZONE_ID, OTR_INTERSTATE_ZONE_ID],
  },
  {
    name: 'OTR - Labor Tracking',
    slug: 'otr-labor-tracking',
    icon: 'pi pi-users',
    component: DashboardOtrLaborTrackingComponent,
    // Requirement: managers and admins, not the admin-only audience of OTR -
    // System Revenue. Its own key (rather than `dashboardSystemRevenue`, which
    // locked managers out, or `dashboardOpsReadiness`, which would open it to
    // agents/estimators/operations) so this audience can be changed from the
    // backend grant lists alone. See OTR_LABOR_DASHBOARD_PERMISSIONS in
    // backend `fusionauth/permissionGraph.ts`.
    permissionsRequired: [FRONTEND_PERMISSIONS.dashboardOtrLaborTracking],
    visibleIn: [YMM_BRAND_ZONE_ID, OTR_INTERSTATE_ZONE_ID],
  },
  {
    name: 'Ops Readiness',
    slug: 'ops-readiness',
    icon: 'pi pi-verified',
    component: DashboardOpsReadinessComponent,
    permissionsRequired: [FRONTEND_PERMISSIONS.dashboardOpsReadiness],
  },
  {
    name: 'OTR - Ops Readiness',
    slug: 'otr-ops-readiness',
    icon: 'pi pi-verified',
    component: DashboardOtrOpsReadinessComponent,
    permissionsRequired: [FRONTEND_PERMISSIONS.dashboardOpsReadiness],
    visibleIn: [YMM_BRAND_ZONE_ID, OTR_INTERSTATE_ZONE_ID],
  },
  {
    name: 'Self Improving AI',
    slug: 'self-improving-ai',
    icon: 'pi pi-sparkles',
    component: DashboardSelfImprovingAIComponent,
    permissionsRequired: [FRONTEND_PERMISSIONS.dashboardSelfImprovingAI],
    helpId: 'dashboard-self-improving-ai',
  },
  {
    name: 'Payments',
    slug: 'payments',
    icon: 'pi pi-credit-card',
    component: DashboardTransactionsComponent,
    permissionsRequired: [FRONTEND_PERMISSIONS.dashboardSystemRevenue],
    display: { showComparedTo: false },
  },
];